import React, { useState, useMemo } from "react";
import { LuUsers, LuStar } from "react-icons/lu";

const formatCurrency = (val) => {
  return `₹${Number(val || 0).toLocaleString("en-IN")}`;
};

const getInitials = (name) => {
  if (!name) return "U";
  return name
    .trim()
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

export default function TeamPerformanceCard({
  organizationUsers = [],
  loading = false,
}) {
  const [sortMode, setSortMode] = useState("deal"); // 'deal' | 'leads' | 'deals' | 'points'

  const sortedUsers = useMemo(() => {
    return [...organizationUsers].sort((a, b) => {
      const aVal = a.deal_price ?? a.price ?? 0;
      const bVal = b.deal_price ?? b.price ?? 0;
      const aLeads = a.leads ?? 0;
      const bLeads = b.leads ?? 0;
      const aDeals = a.deals ?? 0;
      const bDeals = b.deals ?? 0;
      const aPoints = a.points ?? 0;
      const bPoints = b.points ?? 0;

      if (sortMode === "deal") return bVal - aVal;
      if (sortMode === "leads") return bLeads - aLeads;
      if (sortMode === "deals") return bDeals - aDeals;
      if (sortMode === "points") return bPoints - aPoints;
      return 0;
    });
  }, [organizationUsers, sortMode]);

  return (
    <div className="card c8" data-testid="organization-user-table-card">
      <div className="head">
        <h2>
          <LuUsers style={{ color: "#0d9488" }} />
          <span>Team Performance</span>
        </h2>

        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <div className="seg" id="sortSeg">
            <button
              type="button"
              className={sortMode === "deal" ? "on" : ""}
              onClick={() => setSortMode("deal")}
            >
              By Deal Value
            </button>
            <button
              type="button"
              className={sortMode === "leads" ? "on" : ""}
              onClick={() => setSortMode("leads")}
            >
              By Leads
            </button>
            <button
              type="button"
              className={sortMode === "deals" ? "on" : ""}
              onClick={() => setSortMode("deals")}
            >
              By Deals
            </button>
            <button
              type="button"
              className={sortMode === "points" ? "on" : ""}
              onClick={() => setSortMode("points")}
            >
              By Points
            </button>
          </div>
        </div>
      </div>

      <div className="table-wrap">
        <table className="dashboard-table-custom" data-testid="organization-user-table">
          <thead>
            <tr>
              <th>User</th>
              <th className="r">Deal Value</th>
              <th className="r">No of Leads</th>
              <th className="r">No of Deals</th>
              <th className="r">Points Earned</th>
            </tr>
          </thead>
          <tbody id="teamBody">
            {loading && sortedUsers.length === 0 ? (
              Array.from({ length: 4 }).map((_, idx) => (
                <tr key={`skel-team-${idx}`}>
                  <td><div className="skeleton-box" style={{ height: "20px", width: "120px" }} /></td>
                  <td className="r"><div className="skeleton-box" style={{ height: "20px", width: "80px", marginLeft: "auto" }} /></td>
                  <td className="r"><div className="skeleton-box" style={{ height: "20px", width: "40px", marginLeft: "auto" }} /></td>
                  <td className="r"><div className="skeleton-box" style={{ height: "20px", width: "40px", marginLeft: "auto" }} /></td>
                  <td className="r"><div className="skeleton-box" style={{ height: "20px", width: "50px", marginLeft: "auto" }} /></td>
                </tr>
              ))
            ) : sortedUsers.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state-card">
                  No organization/user data available for the selected date range.
                </td>
              </tr>
            ) : (
              sortedUsers.map((u, i) => {
                const val = u.deal_price ?? u.price ?? 0;
                const leads = u.leads ?? 0;
                const deals = u.deals ?? 0;
                const points = Number(u.points ?? 0).toLocaleString("en-US");

                return (
                  <tr key={i} data-testid={`team-row-${i}`}>
                    <td>
                      <div className="user-tag">
                        <div className="user-mini-av">{getInitials(u.user)}</div>
                        <span>{u.user}</span>
                      </div>
                    </td>
                    <td className="r" style={{ fontFamily: "var(--font-mono)", fontWeight: 600 }}>
                      {formatCurrency(val)}
                    </td>
                    <td className="r">
                      <span className="cell-badge">{leads}</span>
                    </td>
                    <td className="r">
                      <span className="cell-badge deal-badge">{deals}</span>
                    </td>
                    <td className="r">
                      <span className="points-pill">
                        <LuStar style={{ width: "11px", height: "11px" }} />
                        {points}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
